import { User, getAuth, signInWithCustomToken, signInWithEmailAndPassword } from 'firebase/auth';
import log from 'loglevel';

import { APP_KEY, CUSTOM_SIGN_IN_ADDRESS, FIREBASE_API_KEY, NODE_ENV } from '../constants';
import { FirebaseModule } from '../firebase';

const userMock = {
  getIdToken: () => undefined,
} as unknown as User;

let user: User | undefined = undefined;

/**
 * Create custom token.
 * @param userKey the user key.
 * @param referer the target origin.
 */
export const createAuthCustomToken = async (userKey: string, referer: string): Promise<string> => {
  // Development
  if (NODE_ENV === 'development' && !FIREBASE_API_KEY) {
    return '';
  }

  if (!FirebaseModule.isReady()) {
    throw new Error('Firebase is not available');
  }

  if (!referer || !userKey) {
    log.warn('using default credentials');
    referer = 'https://thefitsearch.com/';
    userKey = APP_KEY;
  }

  const response = await fetch(
    `${CUSTOM_SIGN_IN_ADDRESS}/vto-custom-sign-in/v1/createCustomToken`,
    {
      headers: {
        'Content-Type': 'application/json',
        'X-Referer': referer,
        'X-User-Key': userKey,
      },
      method: 'POST',
    },
  );

  const { status } = response;
  if (status < 200 || status >= 400) {
    throw new Error();
  }

  const { customToken } = await response.json();
  return customToken;
};

/**
 * Sign in with custom token.
 * @param customToken The custom token.
 */
export const signInAuthCustomToken = async (customToken: string): Promise<User> => {
  if (!FirebaseModule.isReady()) {
    throw new Error('Firebase is not available');
  }

  const auth = getAuth();
  const userCredential = await signInWithCustomToken(auth, customToken);
  user = userCredential.user;
  return user;
};

/**
 * Sign in with email and password.
 * @param email The email.
 * @param password The password.
 */
export const signInEmail = async (email: string, password: string): Promise<User> => {
  if (!FirebaseModule.isReady()) {
    throw new Error('Firebase is not available');
  }

  const auth = getAuth();
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    user = userCredential.user;
  } catch (error) {
    throw new Error(`Could not sign in with Email and Password:${error}`);
  }

  return user;
};

/**
 * Return current user.
 */
export const getAuthUser = (): User | undefined => {
  return user;
};

/**
 * Return mocked user.
 */
export const getAuthUserMock = (): User => {
  return userMock;
};

/**
 * Return auth headers for gcip protected resources.
 * @param user The auth user.
 */
export const getAuthToken = async (): Promise<string | undefined> => {
  const user = getAuthUser();

  if (!user) {
    return undefined;
  }

  // Returns the current token if it has not expired or if it will not expire in the next five minutes. Otherwise, this will refresh the token and return a new one.
  const idToken = await user.getIdToken();

  return idToken;
};
